import sortBy from "lodash/sortBy";
import { convertToAPIDate } from "../utils/convertAPIDate";
import User, { UserResponse } from "../classes/user";
import UserDetails, { UserDetailsResponse } from "../classes/userDetails";
import UserEmployerDetails, { UserEmployerDetailsResponse } from "../classes/userEmployerDetails";
import CoreAPI from "./coreAPI";
import { AccountType } from "../classes/transferDestination";

export type StaticDataList = {
  staticDataList: string[];
};

export default function APIUser(api: CoreAPI) {
  return {
    async getCountriesList() {
      return api.authCall<StaticDataList>({
        method: "GET",
        endpoint: "/api/userDetails/citizenship",
      });
    },

    // Requires ADMIN token
    async removeUser(userId: string) {
      return api.authCall({
        method: "DELETE",
        endpoint: `/api/userIdentity/${userId}`,
      });
    },

    async getEmployerDetails(userId: string) {
      const response = await api.authCall<UserEmployerDetailsResponse>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/employerDetails`,
      });
      return UserEmployerDetails.fromAPI(response);
    },

    async getStatesList() {
      return api.authCall<StaticDataList>({
        method: "GET",
        endpoint: "/api/userDetails/states",
      });
    },

    async getSuffixList() {
      return api.authCall<StaticDataList>({
        method: "GET",
        endpoint: "/api/userDetails/suffix",
      });
    },

    async getPlaidToken(userId: string, accountType: AccountType) {
      const path =
        accountType === AccountType.employerSponsored ? "getAggregatorSessionForES" : "getAggregatorSessionForIRA";
      return api.authCall<string>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/${path}`,
      });
    },

    async getPlaidTokenForUpdate(params: { userId: string; aggregatorLinkId: number }) {
      const { userId, aggregatorLinkId } = params;
      return api.authCall<string>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/getAggregatorSessionInUpdateMode?aggregatorLinkId=${aggregatorLinkId}`,
      });
    },

    async getUser(userId: string) {
      const response = await api.authCall<UserResponse>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}`,
      });
      return User.fromAPI(response);
    },

    async getUserDetails(userId: string) {
      const response = await api.authCall<{
        userDetails: UserDetailsResponse;
      }>({
        method: "GET",
        endpoint: `/api/userIdentity/${userId}/userDetails`,
      });
      if (response.userDetails.employer && (window as any).heap) {
        (window as any).heap.addUserProperties({
          partner: response.userDetails.employer.urlName,
        });
      }
      return UserDetails.fromAPI(response.userDetails);
    },

    async updateUserDetails(params: { userId: string; details: UserDetails }) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/userDetails`,
        body: {
          ...params.details,
          dateOfBirth: convertToAPIDate(params.details.dateOfBirth),
        },
      });
    },

    async updateEmail(params: { userId: string; accessToken: string; newEmail: string }) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/updateEmail`,
        body: params,
      });
    },

    async updatePhoneNumber(params: { userId: string; accessToken: string; newPhoneNumber: string }) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/updatePhoneNumber`,
        body: {
          ...params,
          newPhoneNumber: params.newPhoneNumber,
        },
      });
    },

    async updatePreferences(params: {
      userId: string;
      preferences: {
        hasCurrentEmployerSponsoredAccount: boolean;
        hasIra: boolean;
        hasSixMonthsSavings: boolean;
        loanImportant: boolean;
        tradingFrequency: string;
        tradeIndividualSecurities: any;
        willHaveEmployerSponsoredAccountSoon: boolean;
        financialEducationLevel: string;
      };
    }) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/userPreference`,
        body: params.preferences,
      });
    },

    async uploadSignature(params: { userId: string; signature: any }) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/userIdentity/${params.userId}/userDetails/uploadSignature`,
        body: {
          imageValue: params.signature, // DATA URL,
        },
      });
    },

    async waitListSignUp(email: string, phoneNumber: string, employer: string) {
      return api.call({
        method: "POST",
        endpoint: `/waitlist/notificationSignup`,
        body: {
          email,
          phoneNumber,
          employer,
        },
      });
    },

    async getProvidersList() {
      const response = await api.authCall<{
        staticDataList: string[];
      }>({
        method: "GET",
        endpoint: "/api/retirementSavingsData/providerName",
      });
      if (!response.staticDataList) return [];
      const list = response.staticDataList.map((elem) => ({
        label: elem,
        value: elem,
      }));
      return sortBy(list, (item) => item.label.toLowerCase());
    },

    async createTestScenarios(email: string) {
      return api.authCall({
        method: "POST",
        endpoint: `/api/test/setupData?email=${email}`,
      });
    },
  };
}
